import React, { ReactNode } from 'react';
import autobind from 'autobind-decorator';
import { Table as AntTable, Alert, Input, Row, Col, Button, Dropdown, Menu, Popconfirm, Modal, Select, Form, Card, Tooltip, Upload, Spin, Progress, notification } from 'antd';
import { SortAscendingOutlined, SortDescendingOutlined, FilterOutlined, SaveOutlined, UndoOutlined, EditOutlined, ReloadOutlined, CloudUploadOutlined, DeleteOutlined, SearchOutlined, DownloadOutlined, PlusOutlined, CheckSquareFilled } from '@ant-design/icons';
import { clone, curateString } from 'src/core/utils/object';
import 'src/core/utils/linq';
import { withTranslation, WithTranslation } from 'react-i18next';
import HttpService from "src/core/services/http.service";
import FileSaver from 'file-saver';
import { container } from 'src/inversify.config';
import { withSize } from 'react-sizeme'
import { Query, SortDirection, ListState } from '../../stores/data-store';
import { CommandResult } from '../../stores/types';
import { theme } from 'antd';

let Option = Select.Option;
let Search = Input.Search;


const { getDesignToken } = theme;

export interface TableSortFieldDefinition {
    field: string;
    text: string;
    useProfile: boolean;
}

export interface ImportError {
    row: number;
    reason: string;
}

export interface ImportResult {
    success: boolean;
    totalElements: number;
    importedElements: number;
    errors: ImportError[];
    warnings: ImportError[];
}

export interface TableColumn<T> {
    field: string;
    title: string;
    visible?: boolean;
    searchable?: boolean;
    required?: boolean;
    align?: 'left' | 'right' | 'center';
    sortable?: boolean;
    sortField?: string;
    useProfile?: boolean;
    filter?: TableColumnFilter | React.ReactNode;
    fiterField?: string;
    applyPartialFilter?: boolean;
    filterByQueryString?: boolean;
    customQueryFilter?: (value: string | undefined) => string | undefined;
    queryFilterMode?: CustomFilterField;
    filtered?: boolean;
    multipleFilterMode?: 'All' | 'Any'
    collapsing?: boolean;
    selectableHeader?: boolean;
    headerRenderer?: React.ReactNode;
    renderer?: (item: T) => any;
    editorValuePropName?: string,
    editor?: (item: T) => any;
    fixed?: string;
    width?: number;
}

export interface TableColumnFilter {
    children: React.ReactNode,
}

export interface TableModel<T> {
    query: Query,
    columns: TableColumn<T>[];
    data: ListState<T>;
    sortFields?: TableSortFieldDefinition[];
    rowDisabled?: (row: T) => boolean;
}

export interface DeleteAllOption {
    
}

export type ItemState = 'Unchanged' | 'New' | 'Changed';

export interface TableProps<T> extends WithTranslation {
    error?: string;
    title?: string | React.ReactNode;
    titlePadding?: string;
    rowKey: string;
    embedded?: boolean;
    headerMarginRight?: number;
    headerMarginLeft?: number;
    model: TableModel<T>;
    onRefresh?: () => void;
    canCreateNew?: boolean;
    onNewItem?: () => void;
    newItemName?: string;
    canEdit?: boolean;
    canSort?: boolean;
    onSaveRow?: (item: T, state: ItemState) => Promise<CommandResult<any>>;
    canDelete?: boolean;
    deleteLabel?: string;
    onDeleteRow?: (item: T, state: ItemState) => Promise<CommandResult<any>>;
    onRowClick?: (item: T, state: ItemState) => void;
    searchText?: string
    onQueryChanged: (query: Query) => void;
    searchWidth?: number;
    bulkTemplateUrl?: string;
    bulkInsertUrl?: string;
    bulkTemplateName?: string;
    rightToolbar?: React.ReactNode
    hideFilter?: boolean;
    hidePagination?: boolean;
    hideRefresh?: boolean;
    hideSelection?: boolean;
    onRow?: (record: T, index?: number) => React.HTMLAttributes<HTMLElement>;
    rowClassName?: (record: T, index: number) => string;
    pageSize?: number;
    deleteAllVisible?: boolean;
    showDeleteStatusWindow?: boolean;
    getAllIds?: () => Promise<any>;
    deleteId?: (id: string) => Promise<CommandResult<any>>;
    onUploadBegin?: () => void;
    onUploadEnd?: () => void;
    small?: boolean;
    pageSizeOptions?: string[];
    initPagination?: boolean;
    scroll?: any;
    onOpenDialog?: () => void;
    style?: React.CSSProperties;
    onSelectionChanged?: (rowsKeys: any[], items:T[]) => void | number;
    deleteDisabled?: boolean;
    //onPageChange?: (skip: number, take: number) => void;
    //onSearchFilterChanged?: (q: string) => void;
    //onFilterChanged?: (filterObject: any) => void;
    //onOrderByChanged?: (orderBy: string, orderDirection: SortDirection) => void;
    editLabel?: string;
    //extraActions?: {content: any, onClick: (item: T) => void}[];
    //disallowHidingColumns?: boolean;
    customDeleteMessage?: string;
    activePage?: number;
    selectedRowsNumber?: number;
    showQuickJumper?: boolean;
    selectedRowKeys?: string[];
    newItemButtonRenderer?: (children:ReactNode) => ReactNode;
    searchBarRenderer?: (children: ReactNode) => ReactNode;
}

export interface TableState<T> {
    rowKey: string;
    columns: TableColumn<T>[];
    searchFilter: string;
    data: TableRow<T>[];
    result: CommandResult<any> | undefined;
    showDeleteConfirm: boolean;
    toDelete: TableRow<T> | undefined;
    pageSize: number;
    activePage: number;
    totalPages: number;
    totalItems: number;
    filter: { [id: string]: { key: string, op: string, value: string } };
    selectedRowKeys: any[];
    showConfirmDeletion: boolean;
    showConfirmAllDeletion: boolean;
    editingKey: string;
    filters: any;
    parameters: any;
    isBusy: boolean;
    uploadingError?: any;
    uploadComplete: boolean;
    importResult?: ImportResult,
    loadingMessage: string,
    deleteElements: number;
    deleteElementsDeleted: number;
    onDeleting: boolean;
    deleteFinished: boolean;
    deleteElementsFail: number;
    profileSorterState: {
        field: string;
        direction: SortDirection;
    };
    search: string;
    searchErrorVisible: boolean;
}

type RowMode = 'editor' | 'normal';

export interface TableRow<T> {
    item: T;
    state: ItemState;
    original: T;
    mode: RowMode;
    selected: boolean;
    actionsMenuOpened: boolean;
}

export enum CustomFilterField {
    IgnoreQueryFilter = "ignoreQueryFilter",
    UseCustomQueryFilter = "useCustomFilter",
    SearchInCollection = "searchInCollection",
}

const FormItem = Form.Item;
const EditableContext = React.createContext({});

const EditableRow = (props: any) => {
    const [form] = Form.useForm();
    return (
        <Form form={form} component={false}>
            <EditableContext.Provider value={form}>
                <tr {...props as any} />
            </EditableContext.Provider>
        </Form>
    );
}
const EditableFormRow = EditableRow;

export class EditableCell extends React.Component<any> {
    render() {
        let {
            centered,
            required,
            editing,
            editor,
            dataIndex,
            title,
            inputType,
            record,
            index,
            editorValuePropName,
            ...restProps
        } = this.props;
        return (
            <EditableContext.Consumer>
                {(form: any) => {
                    return (
                        <td {...restProps} className={editing ? `ant-table-cell-editing ${restProps.className}` : restProps.className} style={{ textAlign: centered ? 'center' : '', ...restProps.style }}>
                            {editing && editor ? (
                                <FormItem name={dataIndex}
                                    initialValue={record[dataIndex]}
                                    valuePropName={editorValuePropName || 'value'}
                                    rules={[{
                                        required: required || false,
                                        message: `Field '${title}' is required!`,
                                    }]}
                                    style={{ margin: 0 }}>
                                    {editor(record)}
                                </FormItem>
                            ) : restProps.children}
                        </td>
                    );
                }}
            </EditableContext.Consumer>
        );
    }
}

export interface FilterComponentProps {
    value: any,
    children: any,
    onChange: (value: any) => void;
    t: (s:string) => string;
}

export class FilterComponent extends React.Component<FilterComponentProps> {
    constructor(props: FilterComponentProps) {
        super(props);
        this.state = {}
    }

    @autobind
    public onValueChanged(value: any) {
        this.props.onChange(value?.length === 0 ? undefined : value);
    }

    @autobind
    public onClearFilter() {
        this.props.onChange(undefined);
    }

    public render() {
        if (!this.props.children)
            return <Card>filter not configured</Card>

        return <Card style={{ minWidth: 320 }}>{React.cloneElement(this.props.children as any, { value: this.props.value, onChange: this.onValueChanged })}{this.props.value && <a className='table-filter-clear' style={{ float: 'right', marginTop: 10 }} onClick={this.onClearFilter}>{this.props.t('ClearFilter')}</a>}</Card>
    }
}

export class Table<T> extends React.Component<TableProps<T> & WithTranslation, TableState<T>> {
    private getFilterFromProps<T>(props: TableProps<T>) {
        const model = props?.model;
        if (model == null || (model.columns|| []).length === 0) return {};
        
        const filterables = model.columns.filter(x => x.filter != null).map(x => x.fiterField || x.field)
        if (filterables.length === 0) return {};
        
        const queryFilters = model.query.filter as any;
        if (queryFilters == null) return {};

        const result: any = {};
        for (const key in queryFilters) {
            if (queryFilters.hasOwnProperty(key) && filterables.includes(key)) {
              result[key] = queryFilters[key];
            }
        }

        return result;
    }

    constructor(props: TableProps<T> & WithTranslation) {
        super(props);

        const profileSorterState =  {} as any;
        if ((props?.model?.sortFields || []).length !== 0 && (props?.model?.query?.orderBy || []).length !== 0) {
            const commonField = props.model.sortFields!.find((x) => props.model.query.orderBy!.some(y => y.field === x.field));
            if (commonField != null) {
                profileSorterState.field = commonField.field;
                profileSorterState.direction = props!.model.query.orderBy!.find(y => y.field === commonField.field)?.direction;
            }
        }

        this.state = {
            columns: props.model.columns,
            searchFilter: '',
            data: this.BuildRows(props.model.data),
            result: undefined,
            showDeleteConfirm: false,
            toDelete: undefined,
            activePage: props.activePage || 1,
            pageSize: props.model && props.model.query && props.model.query.take ? props.model.query.take : (props.pageSize ? props.pageSize : 50),
            totalPages: props.model && props.model.data ? Math.ceil((props.model.data == null ? 1 : props.model.data.count) / 10) : 1,
            totalItems: props.model && props.model.data ? props.model.data.count : 0,
            filter: this.getFilterFromProps(props),
            selectedRowKeys: [],
            showConfirmDeletion: false,
            editingKey: '',
            rowKey: props.rowKey || 'id',
            filters: {},
            parameters: {},
            isBusy: false,
            uploadComplete: false,
            loadingMessage: "", 
            showConfirmAllDeletion: false,
            deleteElements: 0,
            deleteElementsDeleted: 0,
            onDeleting: false,
            deleteFinished: false,
            deleteElementsFail: 0,
            profileSorterState,
            search: "",
            searchErrorVisible: false
        };
    }


    UNSAFE_componentWillReceiveProps(nextProps: TableProps<T> & WithTranslation) {
        let activePage= this.state.activePage;

        if (nextProps.initPagination !== this.props.initPagination) {
            this.setState({ activePage : this.props.activePage || 1 });
            activePage = this.props.activePage || 1;
        }

        const pageSize = nextProps.model && nextProps.model.query && nextProps.model.query.take ? nextProps.model.query.take : (nextProps.pageSize ? nextProps.pageSize : 50);
        const totalPages = Math.ceil((nextProps.model.data == null ? 1 : nextProps.model.data.count) / pageSize);
        if (activePage > totalPages) activePage = Math.max(1, activePage - 1)

        let nextState: Partial<TableState<T>> = {
            columns: nextProps.model.columns,
            data: this.BuildRows(nextProps.model.data),
            result: undefined,
            pageSize,
            totalItems: nextProps.model.data.count,
            totalPages,
            filter: this.getFilterFromProps(nextProps),
        };

        if(nextProps.selectedRowKeys !== undefined){
            nextState = {...nextState, selectedRowKeys: nextProps.selectedRowKeys}
        }

        this.setState(nextState as TableState<T>); 
    }

    protected BuildRows(result: ListState<T>): TableRow<T>[] {
        const originalData = this.state && this.state.data ? this.state.data : [];
        const data = result == null ? [] : (result.items || []);
        return data.filter((o) => o).map((o) => {
            let existings = originalData.filter(x => (x.item as any)[this.state.rowKey] === (o as any)[this.state.rowKey])
            let state = existings.length > 0 ? existings[0].state : "Unchanged";
            let original = JSON.parse(JSON.stringify(o))
            let newItem = JSON.parse(JSON.stringify(o))
            return {
                state: state,
                item: newItem,
                original: existings.length > 0 ? existings[0].item : original,
                mode: state === 'New' ? 'editor' : 'normal',
                actionsMenuOpened: false,
                selected: false,
            } as TableRow<T>
        });
    }

    //@autobind
    //protected openNewForm() {
    //    this.props.onNewItem();
    //}

    @autobind
    protected async onDeleteRow() {
        const row = this.state.toDelete;
        if (this.props.onDeleteRow && row) {
            this.hideConfirmModal();
            const result = await this.props.onDeleteRow(row.item, row.state);
            if (result && result.isSuccess) {
                //this.props.model.data.discard(row.item);
                this.setState({ data: this.state.data.filter((o) => o.item !== row.item) });
            }
        }
    }

    //top level orderby
    @autobind
    protected onSortDirection() {
        const sorterState = this.state.profileSorterState;
    
        if (!sorterState?.field) return;
    
        let nextDirection: SortDirection = 'Ascending';
        if (sorterState.direction === 'Ascending') nextDirection = 'Descending';
    
        const query = clone<Query>(this.props.model.query);
        query.orderBy = [
          {
            field: sorterState.field,
            direction: nextDirection,
            useProfile: this.props.model.sortFields?.find((x) => x.field === sorterState.field)?.useProfile ?? false
          }
        ];
    
        this.setState({
          profileSorterState: {
            direction: nextDirection,
            field: sorterState.field
          }
        });
    
        query.tableUpdate = true;
        if (this.props.onQueryChanged) this.props.onQueryChanged(query);
      }

    @autobind
    protected hideConfirmModal() {
        this.setState({ showDeleteConfirm: false, toDelete: undefined });
    }

    //@autobind
    //protected showConfirmModal(row: TableRow<T>) {
    //    row.actionsMenuOpened = false;
    //    this.forceUpdate();
    //    this.setState({showDeleteConfirm: true, toDelete: row});
    //}

    @autobind
    protected handlePaginationChange(currentPage: number) {
        if (currentPage !== this.state.activePage) {
            this.setState({
                activePage: currentPage,
            }, () => {
                const query = clone<Query>(this.props.model.query);
    
                query.skip = (currentPage - 1) * this.state.pageSize;
                query.take = this.state.pageSize;
    
                this.props.model.query = query;

                query.tableUpdate = true;
                if (this.props.onQueryChanged)
                    this.props.onQueryChanged(query);
            });
        }
    }

    @autobind
    protected handlePageSizeChange(pageSize: number) {
        if (pageSize !== this.state.pageSize) {
            this.setState({
                pageSize: pageSize,
                activePage: 1
            });

            const query = clone<Query>(this.props.model.query);

            query.skip = 0;
            query.take = pageSize;

            this.props.model.query = query;

            query.tableUpdate = true;
            if (this.props.onQueryChanged)
                this.props.onQueryChanged(query);
        }
    }

    @autobind
    protected onFilterChanged(filterByQueryString: boolean, filterObject: any, filterChanged: string, partialFilter?: boolean) {
        const query = clone<Query>(this.props.model.query);
        if (filterByQueryString) {
            query.parameters = {...query.parameters, ...filterObject};
        } else {
            if (partialFilter) filterObject[`partial_${filterChanged}`] = filterObject[filterChanged];
            query.filter = {...query.filter, ...filterObject};
        }

        this.setState({
            activePage: 1,
        });

        query.skip = 0;
        query.take = this.state.pageSize;

        query.tableUpdate = true;
        if (this.props.onQueryChanged)
            this.props.onQueryChanged(query);
    }

    //top level orderby
    @autobind
    protected onOrderByChanged(field: string) {
        const query = clone<Query>(this.props.model.query);
    
        if (!field) {
          query.orderBy = [];
          this.setState({ profileSorterState: {} as any });
        } else {
          query.orderBy = [
            {
              field,
              direction: 'Ascending',
              useProfile: this.props.model.sortFields?.find((x) => x.field === field)?.useProfile ?? false
            }
          ];
          this.setState({
            profileSorterState: {
              field,
              direction: 'Ascending'
            }
          });
        }
    
        query.tableUpdate = true;
        if (this.props.onQueryChanged) this.props.onQueryChanged(query);
      }

    @autobind
    protected onSearchFilterChanged(input: string) {
        
        const value = curateString(input, {normalize: true, toLowerCase: true});
        const query = clone<Query>(this.props.model.query);
        if (!query.parameters)
            query.parameters = {} as any;

        if(value) {
            (query.parameters as any)['$search'] = value;
        } else {
            (query.parameters as any)['$search'] = undefined;
        }

        if(!value && input.length !== 0) {
            this.setState({searchErrorVisible: true});
            return;
        }

        const modelQuerySearch = Object.entries(this.props.model.query.parameters ??{}).find(v => v[0] === '$search');
        let modelQuerySearchValue = undefined;
        if(modelQuerySearch && modelQuerySearch[1]){
            modelQuerySearchValue = modelQuerySearch[1]
        }

        if((!modelQuerySearchValue && !value) || (modelQuerySearchValue === value)) {
            return;
        }
        
        this.setState({
            activePage: 1,
            searchErrorVisible: false,
        });

        query.skip = 0;
        query.take = this.state.pageSize;

        query.tableUpdate = true;
        this.props.model.query = query;
        if (this.props.onQueryChanged)
            this.props.onQueryChanged(query);
       
    }

    //protected getOnClick = (data: TableRow<T>) => {
    //    if (this.props.onRowClick == null || data.mode === 'editor' ) {
    //        return null;
    //    } else {
    //        return () => this.props.onRowClick(data.item, data.state);
    //    }
    //}

    //protected addFilter = (id: string, key: string, op: string, value: string, preventReload?: boolean) => {
    //    const filter = this.state.filter;
    //    filter[id] = {key, op, value};
    //    this.setState({filter}, () => {
    //        if (!preventReload && this.props.onFilterChange) {
    //            this.setState({activePage: 1});
    //            const filters = getProperties(filter).map((kvp) => kvp.value);
    //            this.props.onFilterChange(filters);
    //        }
    //    });
    //}

    //protected removeFilter = (id: string, preventReload?: boolean) => {
    //    const filter = this.state.filter;
    //    delete filter[id];

    //    this.setState({filter}, () => {
    //        if (!preventReload && this.props.onFilterChange) {
    //            this.setState({activePage: 1});
    //            const filters = getProperties(filter).map((kvp) => kvp.value);
    //            this.props.onFilterChange(filters);
    //        }
    //    });
    //}

    @autobind
    protected onSelectChange(selectedRowKeys: any[], items:T[]) {        
        if(this.props.onSelectionChanged){
            const result = this.props.onSelectionChanged(selectedRowKeys, items);
            if(result === 0) {
                this.setState({ selectedRowKeys:[] });
            }
            else if(Array.isArray(result)) {
                this.setState({ selectedRowKeys: result })
            } else {
                this.setState({ selectedRowKeys });
            };
        } else this.setState({ selectedRowKeys });
    }

    @autobind
    protected async onDeleteItems() {
        if (this.props.showDeleteStatusWindow) {
            this.setState({ isBusy: true })
            this.setState({ showConfirmDeletion: false })
            if (this.props.onDeleteRow) {

                let deleteok = 0
                let deletefail = 0
                let selectedFail: any[] = []
                const items = []
                for (let i = 0; i < this.state.selectedRowKeys.length; i++) {
                    const key = this.state.selectedRowKeys[i]
                    let item = this.props.model.data.items.filter((o: any) => (o.item as any)[this.state.rowKey as string] === key)[0]
                    if (item) {
                        items.push(item)
                    }
                }
                this.setState({ deleteElements: items.length, deleteElementsDeleted: 0, deleteElementsFail: 0, onDeleting: true, deleteFinished: false })
                for (let i = 0; i < items.length; i++) {

                    let item = items[i]
                    if (item) {
                        let existings = this.state.data.filter(o => (o.item as any)[this.state.rowKey] === (item as any)[this.state.rowKey])
                        let state = existings.length > 0 ? existings[0].state : "Unchanged";
                        const deletion = await this.props.onDeleteRow(item, state).catch(_ => {
                            deletefail++
                            selectedFail.push((item as any)[this.state.rowKey])
                        })
                        if (deletion && deletion.isSuccess) {
                            deleteok++
                        }

                        this.setState({ deleteElementsDeleted: deleteok, deleteElementsFail: deletefail })
                    }
                }
                this.setState({
                    deleteFinished: true, isBusy: false, selectedRowKeys: selectedFail as any[]
                })
                if (deletefail === 0) {
                    this.setState({ onDeleting: false })
                    const args = {
                        message: this.props.t( "DeleteElements"),
                        description:
                            deleteok + " " + this.props.t("ElementsSussessfulyDeleted"),
                        duration: 5,
                    }
                    notification.open(args)
                }

                this.props.model.query.tableUpdate = true;
                this.props.onQueryChanged(this.props.model.query)
                
            }
            
        } else {
            const self = this

            const elementCount = self.props.model.data.items.length
            let deletedElements = 0
            const totalElementCount = self.props.model.data.count

            self.setState({ showConfirmDeletion: false })
            await Promise.all(self.state.selectedRowKeys.map((key: any) => {

                deletedElements++

                let item = self.props.model.data.items.filter((o: any) => (o as any)[self.state.rowKey as string] === key)[0]
                if (item && self.props.onDeleteRow) {
                    let existings = this.state.data.filter(o => (o as any)[this.state.rowKey] === (item as any)[this.state.rowKey])
                    let state = existings.length > 0 ? existings[0].state : "Unchanged";
                    return self.props.onDeleteRow(item, state) as any
                }
                return Promise.resolve() as any
            }))

            if (((self.props.model.query.skip + self.props.model.query.take) >= totalElementCount) && (elementCount === deletedElements)) {
                const newQuery = { ...self.props.model.query }
                newQuery.skip = Math.max(0, newQuery.skip - newQuery.take)
                newQuery.tableUpdate = true;
                this.props.onQueryChanged(newQuery)
            }
            else {
                self.props.model.query.tableUpdate = true;
                this.props.onQueryChanged(self.props.model.query)
            }


            self.setState({ selectedRowKeys: [], showConfirmDeletion: false })
        }
    }

    protected isEditing(record: any) {
        return record.key === this.state.editingKey;
    }

    downloadExcelTemplate = async () => {
        if (this.props.bulkTemplateUrl) {
            let httpService = container.get(HttpService)
            const result = await httpService.get(this.props.bulkTemplateUrl, {
                responseType: 'arraybuffer'
            });
            const blob = new Blob([result.data as any], { type: result.headers['content-type'] });
            (FileSaver as any).saveAs(blob, this.props.bulkTemplateName || "template.xlsx");
        }
    }

    handleMenuClick = (e: any) => {
        switch (e.key) {
            case 'download':
                e.domEvent.stopPropagation();
                this.downloadExcelTemplate();
                break;
        }
    }

    @autobind
    protected onSaveRow(form: any, key: any) {
        form.validateFields().then((values: any) => {
            if (this.props.onSaveRow) {
                const obj = this.state.data.filter(o => (o.item as any)[this.state.rowKey] === key)[0] as any;
                values[this.state.rowKey] = (obj.item as any)[this.state.rowKey];
                this.props.onSaveRow(values, obj.state).then(result => {
                    if (result && result.isSuccess) {
                        obj.item = Object.assign(clone(obj.item) as any, values);
                        obj.state = 'Unchanged';
                        obj.mode = 'normal';
                        this.setState({ editingKey: '' });
                    }
                });
            }
        });
    }

    @autobind
    protected onRowEdit(key: string) {
        this.setState({ editingKey: key });
    }

    @autobind
    protected onRowEditCancelled(form: any, _: string) {
        form.resetFields();
        this.setState({ editingKey: '' });
    }

    @autobind
    private searchAndFilterDisabled() {
        return (this.props?.model?.data?.count === 0 && !(Object.entries(this?.props?.model?.query?.parameters??{})?.find(e=>e[0]==='$search' && e[1]!==undefined) !== undefined) && Object.values(this.state.filter).every(v=>v===undefined))
    }

    // column header level orderby
    @autobind
    protected onChange(pagination: any, __: any, sorter: any) {
        const query = clone<Query>(this.props.model.query);
    
        let field = sorter.field;
        if (!field) return;

        if (!sorter.order) sorter.order = 'ascend';

        query.orderBy = [];
        if (sorter.order) {
          query.orderBy = [
            {
              field: field,
              direction: sorter.order === 'descend' ? 'Descending' : 'Ascending',
              useProfile: this.props.model.columns?.find((x) => x.field === field)?.useProfile ?? false
            }
          ];
        }

        const profileSorterState =  {} as any;

        if ((this.props.model.sortFields || []).length !== 0 && query.orderBy.length !== 0) {
            const commonField = this.props.model.sortFields!.find((x) => query.orderBy![0].field === x.field);
            if (commonField != null) {
                profileSorterState.field = commonField.field;
                profileSorterState.direction = query.orderBy[0].direction;
            }
        }

        query.skip = (pagination.current - 1) * pagination.pageSize;
        query.take = pagination.pageSize;

        query.tableUpdate = true;
        this.props.model.query = query;
		
		this.setState({ profileSorterState, activePage: pagination.current, pageSize: pagination.pageSize }, () => {
            if (this.props.onQueryChanged) this.props.onQueryChanged(query);
        });
      }

    //@autobind
    //protected onDiscardRow(row: TableRow<T>) {
    //    if (row.state === 'New') {
    //        this.props.model.data.discard(row.item);
    //        this.setState({ data: this.state.data.filter((o) => o !== row) });
    //    } else {
    //        row.mode = 'normal';
    //        row.selected = false;
    //        row.item = clone(row.original);
    //        this.forceUpdate();
    //    }
    //}
    protected uploadComplete(response: ImportResult) {

        this.setState({ importResult: response, uploadComplete: true });
    }

    protected async OnClickDeleteAll() {
        this.setState({ isBusy: true })
        if (this.props.getAllIds && this.props.deleteId) {
            const allIds = await this.props.getAllIds();
            if ((allIds.data as any).success) {
                const ids = ((allIds.data as any).ids as string[])
                this.setState({ deleteElements: ids.length, deleteElementsDeleted: 0, deleteElementsFail: 0, showConfirmAllDeletion : false, onDeleting: true, deleteFinished : false })
                let deleteok = 0;
                let deletefail = 0;
                for (let i = 0; i < ids.length; i++) {
                    const id = ids[i]
                    const deletion = await this.props.deleteId(id).catch(reason => {
                        deletefail++;
                    });
                    if (deletion && deletion.isSuccess) {
                        deleteok++
                    }
                    this.setState({ deleteElementsDeleted: deleteok, deleteElementsFail: deletefail})
                }
                this.setState({
                    deleteFinished : true , isBusy : false
                })
                if (deletefail === 0) {
                    this.setState({ onDeleting: false })
                    const args = {
                        message: this.props.t("DeleteElements"),
                        description:
                            deleteok + " " + this.props.t("ElementsSussessfulyDeleted"),
                        duration: 5,
                    }
                    notification.open(args)
                }

                this.props.model.query.tableUpdate = true
                this.props.onQueryChanged(this.props.model.query);
            }
        }

    }


    downloadErrorsTxtFile = () => {
        if (this.state.importResult && this.state.importResult.errors.length > 0) {
            const element = document.createElement("a");
            const file = new Blob(this.state.importResult.errors.map(o => o.reason + "\n") as any, { type: 'text/plain' });
            element.href = URL.createObjectURL(file);
            element.download = "errors.txt";
            document.body.appendChild(element); // Required for this to work in FireFox
            element.click();
        }
    }

    downloadWarningsTxtFile = () => {
        if (this.state.importResult && this.state.importResult.errors.length > 0) {
            const element = document.createElement("a")
            const file = new Blob(this.state.importResult.warnings.map(o => o.reason + "\n") as any, { type: 'text/plain' })
            element.href = URL.createObjectURL(file)
            element.download = "warnings.txt"
            document.body.appendChild(element) // Required for this to work in FireFox
            element.click()
        }
    }

    public render() {
        const { t } = this.props as any;
        let columns = [];
        if (this.props.canEdit) {
            columns.push({
                title: "",
                width: 50,
                maxWidth: 50,
                dataIndex: 'operation',
                render: (text: string, record: any) => {
                    const editable = this.isEditing(record);
                    return (
                        <div style={{ width: editable ? 50 : 'auto' }}>
                            {editable ? (
                                <span>
                                    <EditableContext.Consumer>
                                        {form => {
                                            return (
                                            <>
                                                <a
                                                    href="javascript:;"
                                                    onClick={() => this.onSaveRow(form, record.key)}
                                                    style={{ marginRight: 8 }}>
                                                    <SaveOutlined />
                                                </a>
                                                <Popconfirm
                                                        title={this.props.t("AreYouSureYouWantToDiscard")}
                                                    onConfirm={() => this.onRowEditCancelled(form, record.key)}
                                                >
                                                    <a><UndoOutlined /></a>
                                                </Popconfirm>
                                            </>);
                                        }}
                                    </EditableContext.Consumer>
                                </span>
                            ) : (
                                    <a onClick={() => this.onRowEdit(record.key)}><EditOutlined /></a>
                                )}
                        </div>
                    );
                },
            } as any);
        }
        const self = this;


        const width = (this.props as any).size.width;
        const isMobile = width < 480;
        const isTablet = width >= 480 && width < 1024;

        columns = columns.concat(this.state.columns.filter((c) => c.visible || c.visible === undefined).map(c => {
            let sorField = this.props.model.query.orderBy ? this.props.model.query.orderBy.firstOrDefault(o => o.field.toLowerCase() === ((c.sortField && c.sortField.toLowerCase()) || (c.field && c.field.toLowerCase()))) : undefined;
            return {
                title: <>
                    {c.searchable && <SearchOutlined style={{ color: '#1890ff' }} />}
                    {c.headerRenderer || t(c.title)}
                </>,
                dataIndex: c.field || c.title,
                editorValuePropName: c.editorValuePropName,
                key: c.field || c.title,
                width: c.width || 'auto',
                fixed: c.fixed !== undefined ? c.fixed : 'left',
                align: c.align || 'left',
                onCell: (record: any) => ({
                    record,
                    required: c.required,
                    align: c.align,
                    editorValuePropName: c.editorValuePropName,
                    dataIndex: c.field || c.title,
                    title: c.title,
                    editor: c.editor,
                    editing: this.isEditing(record),
                }),
                sorter: this?.props?.model?.data?.count === 0 ? false: c.sortable || false,
                filterDropdownProps: {
                    placement: isMobile? 'bottom' : 'bottomRight',
                },
                sortOrder: c.sortable && sorField ? (sorField.direction === 'Ascending' ? 'ascend' : 'descend') : false,
                render: (text: string, record: any, index: number) => c.renderer === undefined ? <span style={{ textAlign: c.align || 'left' }}>{text}</span> : <div style={{ textAlign: c.align || 'left' }}>{c.renderer(record)}</div>,
                ...((!c.filter || (this.searchAndFilterDisabled()))? undefined : {
                    filteredValue: self.state.filter[c.fiterField || c.field] || null,
                    filterDropdown: (o: any) => (<FilterComponent t={t} onChange={value => {
                        const change = {} as any;
                        if(c.queryFilterMode) {
                            if(c.queryFilterMode === CustomFilterField.UseCustomQueryFilter) {
                                if(c.customQueryFilter && c.customQueryFilter(value)) {
                                    change[c.fiterField || c.field] = {and: c.customQueryFilter(value)};
                                } else {
                                    change[c.fiterField || c.field] = undefined;
                                };
                            } else if(c.queryFilterMode === CustomFilterField.IgnoreQueryFilter) {
                                delete change[c.fiterField || c.field]
                            } else if(c.queryFilterMode === CustomFilterField.SearchInCollection) {
                                const fields = (c.fiterField || c.field).split("/")
                                const searchField = fields.pop()
                                const baseField = fields.join("/")
                                const singleFilter = value?{and: `${baseField}/any(x:x/${searchField} eq ${value})`}: undefined;
                                if(Array.isArray(value)) {
                                    const multipleFilters = value.map(v=>`${baseField}/any(x:x/${searchField} eq ${v})`)
                                    const multipleFilterSelector = c.multipleFilterMode === "All" ? {and: multipleFilters} : {or: multipleFilters}
                                    change[c.fiterField || c.field] = {and: multipleFilterSelector};
                                } else {
                                    change[c.fiterField || c.field] = singleFilter;
                                }
                            }
                        } else {
                            if(Array.isArray(value)) {
                                const filter = value.map(v=>`${c.field || c.field} eq '${v}'`)
                                change[c.fiterField || c.field] = {and: {or: filter}};
                            } else {
                                change[c.fiterField || c.field] = value;
                            }
                        }


                        const filterObject = c.filterByQueryString ? { ...self.state.parameters, ...change } : { ...self.state.filter, ...change };
                                                
                        self.setState({ filter: filterObject });
                        self.onFilterChanged(c.filterByQueryString || false, filterObject, (c.fiterField || c.field), c.applyPartialFilter);
                        //confirm();
                    }} value={c.queryFilterMode ? undefined : self.state.filter[c.fiterField || c.field]}>{c.filter}</FilterComponent>),
                    filterIcon: (_: any) => {
                        const isFilterd = c.filtered !== undefined ? c.filtered : !!self.state.filter[c.fiterField || c.field]
                        return <FilterOutlined style={{ color: isFilterd ? '#1890ff' : undefined }} />
                    },
                })
            };
        }));

        const currentItems = this.state.activePage * this.state.pageSize > this.props.model.data.count
                ? this.props.model.data.count % this.state.pageSize
                : this.state.pageSize;
        
        const itemsTotalMessage = t('PaginationTotal').replace("{0}", currentItems).replace("{1}", this.state.totalItems)

        let pagination = {
            size: (isMobile || isTablet) ? 'small' : undefined,
            showTotal: (total: number) => itemsTotalMessage,
            showSizeChanger: !isMobile,
            showQuickJumper: this.props.showQuickJumper !== undefined ? this.props.showQuickJumper : !isMobile,
            onShowSizeChange: (_: number, pageSize: number) => {
                this.handlePageSizeChange(pageSize);
            },
            onChange: (page: number) => {
                this.handlePaginationChange(page);
            },
            pageSizeOptions: this.props.pageSizeOptions || ['10', '25', '50'],
            pageSize: this.state.pageSize,
            total: this.state.totalItems,
            //showTotal: (total: number, range: any) => isMobile ? '' : `${t("Total Rows")}: ${total}`,
            current: this.state.activePage
        };

        let rowKey = 1;
        const tableData = this.state.data.map(o => {
            const item = clone<T>(o.item);
            (item as any).key = (item as any)[this.state.rowKey] || rowKey;
            if(this.props.model.rowDisabled){
                (item as any).disabled = this.props.model.rowDisabled(item)
            }
            rowKey++;
            return item;
        });

        const { selectedRowKeys } = this.state;
        const rowSelection = (this.props.canEdit || this.props.canDelete) ? {
            selectedRowKeys,
            onChange: this.onSelectChange,
            preserveSelectedRowKeys: true,
            getCheckboxProps: (record: any) => ({
                disabled: record.disabled, 
                name: record.name, 
            }),
        } : null;
        const hasSelected = selectedRowKeys.length > 0;

        const components = {
            body: {
                row: EditableFormRow,
                cell: EditableCell,
            },
        };

        let canEmbedd = this.props.embedded && (!this.state.result || this.state.result.isSuccess);
        const menu = (
            <Menu onClick={this.handleMenuClick}>
                {this.props.bulkTemplateUrl && <Menu.Item key="download"><DownloadOutlined />{t('DownloadTemplate')}</Menu.Item>}
            </Menu>
        );
        
        // if (this.state.importResult != null && this.state.importResult.totalElements && this.state.importResult.importedElements) {
        //     const totalElements = this.props.t("Total Elements : ") + this.state.importResult.totalElements;
        //     const importedElements = t("Imported Elements : ") + this.state.importResult.importedElements;
        // }

        const isRightToolBarHidden = () => {
            var x = this.props.rightToolbar;
            return hasSelected && !this.props.editLabel;
        }
        return (
            <div>
               
                <Modal
                    open={this.state.showConfirmAllDeletion}
                        closable={false}
                        onOk={()=> this.OnClickDeleteAll()}
                        onCancel={() => this.setState({ showConfirmAllDeletion: false })}
                        okText={t("Ok")}
                        cancelText={t("Cancel")}
                        title={t('ConfirmationRequired')}>
                        <p>{t("AreYouSureYouWantToDelete")}</p>
                    </Modal>
                
                <Modal
                    open={this.state.showConfirmDeletion}
                    closable={true}
                    onOk={this.onDeleteItems}
                    onCancel={() => this.setState({ showConfirmDeletion: false })}
                    okText={t("DeleteItems")}
                    okButtonProps={{ style: { width: 150 }}}
                    cancelText={t("Cancel")}
                    cancelButtonProps={{ type: 'default', style: { width: 150 } }}
                    title={<h3 style={{ margin: '12px 16px' }}>{`${t('Confirmation')}`}</h3>}>
                    <p style={{ margin: '12px 16px' }}>{this.props.customDeleteMessage? this.props.customDeleteMessage: t("AreYouSureYouWantToDeleteSelected")}</p>
                </Modal>
                <Modal
                    open={this.state.uploadComplete}
                    closable={true}
                    onOk={() => this.setState({ uploadComplete: false })}
                    onCancel={() => this.setState({ uploadComplete: false })}
                    okText={t("Ok")}

                    cancelButtonProps={{ hidden: t}}
                    title={t('UploadComplete')}>
                    <div>
                        {this.state.importResult && this.state.importResult.totalElements &&
                            <>  <Alert message={<p>{t("TotalElements")} : {this.state.importResult.totalElements}</p>} banner type="info"
                                showIcon />
                                <Alert message={<p>{t("TotalImported")} : {this.state.importResult.importedElements}</p>} banner type="info"
                                    showIcon />
                            </>
                        }

                        {this.state.importResult && this.state.importResult.errors && this.state.importResult.errors.length > 0 &&
                            <>
                            <Button onClick={this.downloadErrorsTxtFile}>{t("DownloadErrorsFile")}</Button>
                                <br></br>
                                <Alert message={t("Errors")} description={this.state.importResult.errors.map(o => <> <p> {o.reason}</p> </>)} banner type="error" />

                            </>
                        }

                        {this.state.importResult && this.state.importResult.warnings && this.state.importResult.warnings.length > 0 &&
                            <>
                            <Button onClick={this.downloadWarningsTxtFile}>{t("DownloadWarningsFile")}</Button>
                            <br></br>
                            <Alert message={t("Warnings")} description={this.state.importResult.warnings.map(o => <> <p> {o.reason}</p> </>)} banner type="warning" />

                            </>
                        }


                    </div>

                </Modal>
                <Modal
                    open={this.state.onDeleting}
                    closable={false}
                    onOk={() => this.setState({ onDeleting: false })}
                    onCancel={() => this.setState({ onDeleting: false })}
                    okText={t("Ok")}
                    cancelButtonProps={{ hidden: true }}
                    okButtonProps={{ hidden: !this.state.deleteFinished && this.state.deleteElementsFail === 0 }}
                    title={t('DeleteElements')}>
                    <div>
                        <div style={{ textAlign: "center" }}>
                            <Progress type="circle" percent={this.state.deleteElements === 0 ? 0 : Math.round((this.state.deleteElementsDeleted + this.state.deleteElementsFail) * 100 / this.state.deleteElements)} />
                        </div>
                        <br></br>
                            <>  <Alert message={<p>{t("DeletedElements")} : {this.state.deleteElementsDeleted}</p>} banner type="info"
                            showIcon />
                            {this.state.deleteElementsFail > 0 && <Alert message={<p>{t("FailDeletion")} : {this.state.deleteElementsFail}</p>} banner type="error"
                                showIcon />}
                        </>
                    </div>

                </Modal>
                <Spin spinning={(this.props.model.data && this.props.model.data.isBusy) || this.state.isBusy} tip={this.state.loadingMessage}>
                    <div className="table-header" style={{ marginTop: canEmbedd ? -65 : 0, marginBottom: canEmbedd ? 16 : 0, marginRight: this.props.headerMarginRight ? this.props.headerMarginRight : 0, marginLeft: this.props.headerMarginLeft ? this.props.headerMarginLeft : 0 }}>

                        <Row style={{ padding: this.props.titlePadding ?? 40 }}>
                            <Col span={12}>
                                <ul className="toolbar" style={{ float: 'left', width: '100%' }}>
                                    {!isMobile && this.props.model.sortFields && this.props.model.sortFields.length > 0 && (this.props.canSort === undefined || this.props.canSort) && <li>
                                        <Select dropdownMatchSelectWidth={false} allowClear style={{ height: 36 }} placeholder={t('OrderBy')} value={this.state.profileSorterState?.field ?? undefined} onChange={(value: any) => this.onOrderByChanged(value as string)}>
                                            {(this.props.model.sortFields || []).map(o => <Option key={o.field} value={o.field}>{o.text}</Option>)}
                                        </Select>
                                    </li>}
                                    {!isMobile && this.state.profileSorterState.field && (this.props.canSort === undefined || this.props.canSort) && <li>
                                        <Button onClick={e => this.onSortDirection()}>
                                            {(this.state.profileSorterState.direction || 'Ascending') === 'Ascending' ? <SortAscendingOutlined />: <SortDescendingOutlined />}
                                        </Button>
                                    </li>}
                                    {!hasSelected && !this.props.title && !(isMobile||isTablet) && !this.props.hideFilter && < li style={{ width: '100%' }} >
                                        {this.props.searchBarRenderer ? this.props.searchBarRenderer(
                                            <Input.Search
                                                disabled={this.searchAndFilterDisabled()}
                                                onChange={(v) => this.setState({search: v.target.value})}
                                                value={this.state.search}
                                                className='main-table-search'
                                                prefix={<SearchOutlined />}
                                                allowClear
                                                placeholder={this.props.searchText || t('Search') +'...'}
                                                style={{ width: '100%'}}
                                                onSearch={(search: string) => {this.onSearchFilterChanged(search)}}
                                            />
                                        ) : (
                                            <Input.Search
                                                disabled={this.searchAndFilterDisabled()}
                                                onChange={(v) => this.setState({search: v.target.value})}
                                                value={this.state.search}
                                                className='main-table-search'
                                                prefix={<SearchOutlined />}
                                                allowClear
                                                placeholder={this.props.searchText || t('Search') +'...'}
                                                style={{ width: '100%'}}
                                                onSearch={(search: string) => {this.onSearchFilterChanged(search)}}
                                            />
                                        )}
                                        
                                        {this.state.searchErrorVisible &&  <span style={{color:getDesignToken().colorError}}>{t('PleaseEnterAValidSearchTerm')}</span>}
                                    </li>}
                                    {!hasSelected && this.props.title && <li>{((typeof this.props.title === 'string' || this.props.title instanceof String) ? <h3 style={{ margin: '12px 0' }}>{this.props.title}</h3> : this.props.title)}</li>}
                                    {hasSelected && <h3 style={{ marginTop: 12 }}>{this.props.selectedRowsNumber !== undefined ? this.props.selectedRowsNumber: selectedRowKeys.length} {t('rowsSelected')}</h3>}
                                    {hasSelected && <CheckSquareFilled style={{ marginLeft: 15, marginBottom: 10, marginTop: 12, fontSize: 18, color: '#a6192e' }} />}
                                </ul>
                            </Col>
                            <Col span={12}>
                                <ul className="toolbar" style={{ float: 'right' }}>
                                    {!hasSelected && this.props.title && !(isMobile||isTablet) && !this.props.hideFilter && < li style={{ width: '100%' }} >
                                        {this.props.searchBarRenderer?this.props.searchBarRenderer(
                                            <Input.Search
                                                disabled={this.searchAndFilterDisabled()}
                                                onChange={(v) => this.setState({search: v.target.value})}
                                                value={this.state.search}
                                                prefix={<SearchOutlined />}
                                                className='main-table-search'
                                                allowClear
                                                placeholder={this.props.searchText || t('Search')}
                                                style={{ width: '100%' }}
                                                onSearch={(search: string) => this.onSearchFilterChanged(search)}
                                            />
                                        ):(
                                            <Input.Search
                                                disabled={this.searchAndFilterDisabled()}
                                                onChange={(v) => this.setState({search: v.target.value})}
                                                value={this.state.search}
                                                prefix={<SearchOutlined />}
                                                className='main-table-search'
                                                allowClear
                                                placeholder={this.props.searchText || t('Search')}
                                                style={{ width: '100%' }}
                                                onSearch={(search: string) => this.onSearchFilterChanged(search)}
                                            />
                                        )}
                                        {this.state.searchErrorVisible &&  <span style={{color:getDesignToken().colorError}}>{t('PleaseEnterAValidSearchTerm')}</span>}
                                    </li>}
                                    {this.props.rightToolbar && <ul className="toolbar" hidden={isRightToolBarHidden()} style={{ float: 'right', marginRight: 5 }}>
                                        {this.props.rightToolbar}
                                    </ul>}
                                    {!hasSelected && !this.props.hideRefresh && < li > <Button className="ant-btn-secondary" style={{ width: 56}} onClick={this.props.onRefresh}><ReloadOutlined style={{ color: "#a6192e" }} /></Button></li>}
                                    {!hasSelected && this.props.canCreateNew && this.props.onNewItem && 
                                    (<li>
                                        {this.props.newItemButtonRenderer && this.props.newItemButtonRenderer(
                                            <Tooltip placement="topLeft" title={t('AddNew')}>
                                                <Button type="primary" style={{ width: 'auto', minWidth: 'max-content' }} icon={<PlusOutlined style={{ color: "white" }} />} onClick={this.props.onNewItem}>
                                                    {this.props.newItemName}
                                                </Button>
                                            </Tooltip>
                                        )}
                                        {!this.props.newItemButtonRenderer && (
                                            <Tooltip placement="topLeft" title={t('AddNew')}>
                                                <Button type="primary" style={{ width: 'auto', minWidth: 'max-content' }} icon={<PlusOutlined style={{ color: "white" }} />} onClick={this.props.onNewItem}>
                                                    {this.props.newItemName}
                                                </Button>
                                            </Tooltip>
                                        )}
                                    </li>)
                                    }
                                    {hasSelected && this.props.canDelete &&  <li><Tooltip placement="topLeft" title={t('DeleteSelected')}><Button onClick={() => this.setState({ showConfirmDeletion: true })} disabled={this.props.deleteDisabled} type="primary" style={{ width: 56 }}><DeleteOutlined style={{ fontSize: 18 }} /></Button></Tooltip></li>}
                                  
                                    {this.props.bulkInsertUrl && <li><Upload
                                        name="file"
                                        headers={{
                                            authorization: `Bearer ${HttpService.accessToken}`,
                                            language: `${ HttpService.language}`
                                        }}
                                        showUploadList={false}
                                        action={this.props.bulkInsertUrl}
                                        onChange={info => {
                                            if (info.file.status === 'uploading')
                                            {
                                                this.setState({ isBusy: true })
                                                if (this.props.onUploadBegin)
                                                    this.props.onUploadBegin();
                                            }
                                            else {
                                                this.setState({ isBusy: false });
                                            }
                                            if (info.file.status === 'error' || (info.file.response && info.file.response.messages && info.file.response.messages.length > 0)) {
                                                if (!info.file.response.error)
                                                    this.setState({ uploadingError: info.file.response })
                                                else
                                                    this.setState({ uploadingError: info.file.response.error ? info.file.response.error.message : info.file.response.messages.map((i: any) => i.body).join(",") })
                                            } else {
                                                this.setState({ uploadingError: undefined })
                                            }
                                            if (info.file.status === 'done') {
                                                this.uploadComplete(info.file.response);
                                                if (this.props.onUploadEnd)
                                                    this.props.onUploadEnd();
                                                if (this.props.onRefresh)
                                                    this.props.onRefresh();
                                            }
                                        }}>
                                        <Tooltip title={t('Bulk upload items')}>
                                            <Dropdown.Button overlay={menu}>
                                                <CloudUploadOutlined />
                                            </Dropdown.Button>
                                        </Tooltip>
                                        </Upload></li>}

                                        {this.props.deleteAllVisible && <li><Tooltip placement="topLeft" title={t('DeleteAll')}><Button onClick={() => this.setState({ showConfirmAllDeletion: true })} type='primary' danger><DeleteOutlined />{this.props.t("DeleteAll")}</Button></Tooltip></li>}
                                </ul>
                            </Col>
                            {!hasSelected && !this.props.title && (isMobile||isTablet) && !this.props.hideFilter && < li style={{ width: '100%' }} >
                                <Col span={24}>
                                    {this.props.searchBarRenderer?this.props.searchBarRenderer(
                                        <Input.Search
                                            disabled={this.searchAndFilterDisabled()}
                                            onChange={(v) => this.setState({search: v.target.value})}
                                            value={this.state.search}
                                            allowClear
                                            className='main-table-search'
                                            prefix={<SearchOutlined />}
                                            placeholder={this.props.searchText || t('Search') +'...'}
                                            style={{ width: '100%', top: "10px", marginTop: "10px" }}
                                            enterButton={undefined}
                                            onSearch={(search: string) => this.onSearchFilterChanged(search)}
                                        />
                                    ):(
                                        <Input.Search
                                            disabled={this.searchAndFilterDisabled()}
                                            onChange={(v) => this.setState({search: v.target.value})}
                                            value={this.state.search}
                                            allowClear
                                            className='main-table-search'
                                            prefix={<SearchOutlined />}
                                            placeholder={this.props.searchText || t('Search') +'...'}
                                            style={{ width: '100%', top: "10px", marginTop: "10px" }}
                                            enterButton={undefined}
                                            onSearch={(search: string) => this.onSearchFilterChanged(search)}
                                        />
                                    )}
                                    {this.state.searchErrorVisible &&  <span style={{color:getDesignToken().colorError}}>{t('PleaseEnterAValidSearchTerm')}</span>}
                                </Col>
                            </li>}
                        </Row>
                    </div>
                <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                    {this.state.uploadingError && 
                        <Alert
                            type='error'
                            message={t('AnErrorOcurred')}
                            description={this.state.uploadingError} />
                    }
                    {this.state.result && !this.state.result.isSuccess &&
                        <Alert type='error' style={{ marginBottom: 16 }}
                            message={t('AnErrorOcurred')}
                            description={this.state.result.messages.map((o) => o.body)}
                        />
                    }
                    {this.props.error &&
                        <Alert type='error' style={{ marginBottom: 16 }}
                            message={t('AnErrorOcurred')}
                            description={this.props.error}
                        />
                    }
                </div>
                <AntTable
                        sortDirections={['ascend', 'descend', 'ascend']}
                        scroll={this.props.scroll}
                        onChange={this.onChange}
                        components={components}
                        rowSelection={this.props.hideSelection ? undefined : rowSelection as any}
                        columns={columns}
                        size={this.props.small ? "small" : undefined}
                        onRow={this.props.onRow as any}
                        rowClassName={this.props.rowClassName as any}
                        locale={{
                            emptyText: t('NoData'),
                            filterReset: t('ResetFilter'),
                            selectAll: t('SelectAll'),
                            filterConfirm: t('FilterConfirm'),
                            filterTitle: t('Filter'),
                            selectInvert: t("InvertSelection"),
                            sortTitle: t('Sort')
                        }}
                        pagination={this.props.hidePagination ? false : pagination as any}
                        dataSource={tableData as any}
                        style={this.props.style ?? { margin: '0 40px' }}
                        />
                </Spin>
            </div>
        );


    }
}

export const TableView = withTranslation()(withSize()(Table)) as any;
